import cn from "classnames";
import { useContext, useState } from "react";
import {
  CheckboxProps as CheckboxPropsPrimitiveProps,
  Checkbox as CheckboxPrimitive,
  CheckboxGroupStateContext,
} from "react-aria-components";

import { getFocusOutlineProps } from "@/components/FocusOutline/FocusOutline";
import { InputBorder } from "@/components/InputBorder/InputBorder";
import { getInputBorderState } from "@/components/InputBorder/InputBorder.utils";
import { Stack } from "@/components/Stack/Stack";
import { SVGCheckMark } from "@/components/SVGIcon/static/SVGCheckMark";
import { mergeProps } from "@/utils/reactExtensions";

import styles from "./Checkbox.module.scss";

export interface CheckboxProps extends CheckboxPropsPrimitiveProps {
  /**
   * The value of the checkbox button, used when submitting an HTML form (controlled). See MDN.
   */
  value?: CheckboxPropsPrimitiveProps["value"];

  /**
   * Whether the element should be selected (controlled).
   */
  isSelected?: CheckboxPropsPrimitiveProps["isSelected"];

  /**
   * Whether the element should be selected (uncontrolled).
   */
  defaultSelected?: CheckboxPropsPrimitiveProps["defaultSelected"];

  /**
   * Whether the checkbox button is disabled or not.
   * Shows that a selection exists, but is not available in that circumstance.
   */
  isDisabled?: CheckboxPropsPrimitiveProps["isDisabled"];

  /**
   * Whether the element has frame or not.
   */
  isFramed?: boolean;

  /**
   * Ref to checkbox element.
   */
  inputRef?: React.MutableRefObject<HTMLInputElement>;

  /**
   * Whether the element should receive focus on render.
   */
  autoFocus?: CheckboxPropsPrimitiveProps["autoFocus"];

  /**
   * Variant of the checkbox icon size. Available options are `default` and `small`.
   */
  variant?: "default" | "small";
  children: React.ReactNode;
}

/**
 * Checkboxes are an element used for binary choices where there is only one answer, either ‘yes’ (checked) or ‘no’
 * (unchecked). Checking a check box is an action which will occur on a delay rather than immediately (like a switch
 * would) often requiring the user to trigger a submit button before they come into affect.
 *
 * They’re generally used within forms.
 *
 * Within a checkbox group, multiple checkboxes allow users to choose more than one answer.
 *
 * ## Note
 * - It is recommended to use this in a `CheckboxGroup` component.
 *
 * ## Usage
 *
 * ```tsx
 *  <Checkbox>My Checkbox Option</Checkbox>
 * ```
 */
export const Checkbox = /*#__PURE__*/ (function () {
  const Checkbox: React.FC<CheckboxProps> = ({
    isFramed,
    children,
    onChange,
    variant,
    ...props
  }) => {
    const checkboxGroupState = useContext(CheckboxGroupStateContext);
    const isInvalid = checkboxGroupState?.isInvalid || props.isInvalid;
    const isDisabled = checkboxGroupState?.isDisabled || props.isDisabled;
    const isDefault = isInvalid === undefined;
    const [isSelected, setIsSelected] = useState(
      props.isSelected || checkboxGroupState?.value.includes(props.value || "") || false
    );
    const isSmallVariant = variant === "small";

    const handleOnChange = (value: boolean) => {
      setIsSelected(!isSelected);
      onChange && onChange(value);
    };

    const renderContent = (
      <CheckboxPrimitive
        {...props}
        className={cn(styles.checkbox, { [styles.isFramed]: isFramed })}
        onChange={handleOnChange}
      >
        <Stack
          direction="row"
          spacing={isSmallVariant ? "2xs" : "xs"}
          alignItems="center"
          className={styles.stack}
        >
          <div
            className={cn(styles.iconContainer, {
              [styles.smallIconContainer]: isSmallVariant,
            })}
          >
            <InputBorder
              component="div"
              isHoverStateDisabled={isDisabled || !isDefault}
              state={getInputBorderState({ isInvalid, isDefault })}
              {...mergeProps(getFocusOutlineProps({}), {
                className: styles.checkboxTickContainer,
              })}
            >
              <SVGCheckMark
                className={cn(styles.checkboxTick, {
                  [styles.smallCheckboxTick]: isSmallVariant,
                })}
              />
            </InputBorder>
          </div>

          {children}
        </Stack>
      </CheckboxPrimitive>
    );
    return isFramed ? (
      <InputBorder
        isHoverStateDisabled={true}
        state={getInputBorderState({ isInvalid, isSelected, isDefault })}
        borderRadius="medium"
      >
        {renderContent}
      </InputBorder>
    ) : (
      renderContent
    );
  };

  Checkbox.displayName = "Checkbox";

  return Checkbox;
})();
